import { useState } from 'react';

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'Can I sell an inherited house before probate is complete?',
      answer: 'In most cases, you cannot legally sell an inherited property until probate is complete and ownership has been officially transferred to you. However, we can still make you an offer during the probate process, and we\'ll work with your probate attorney to ensure the sale proceeds as soon as legally possible. In some situations, the executor or administrator of the estate may be able to sell the property with court approval before probate closes. Our team will help you understand your specific situation and timeline.'
    },
    {
      question: 'What if there are multiple heirs who inherited the property?',
      answer: 'When multiple heirs inherit a property, all heirs must agree to the sale. We have extensive experience working with multiple heirs and can facilitate solutions that work for everyone. If some heirs want to sell and others don\'t, we can sometimes arrange to buy out individual shares. We can also help mediate discussions and provide fair market valuations that help all parties reach an agreement. Our goal is to find a solution that satisfies everyone while preserving family relationships.'
    },
    {
      question: 'Do I have to pay capital gains tax when selling an inherited home?',
      answer: 'Inherited properties receive a "step-up in basis," which means the property\'s value is reset to its fair market value at the time of the previous owner\'s death. This often significantly reduces or eliminates capital gains tax when you sell. For example, if your parent bought the house for $100,000 but it was worth $250,000 when they passed away, your basis is $250,000. If you sell it for $260,000, you only pay capital gains tax on $10,000. We recommend consulting with a tax professional about your specific situation, but in many cases, selling an inherited property has minimal tax consequences.'
    },
    {
      question: 'What happens if the inherited property has a mortgage or liens?',
      answer: 'Outstanding mortgages and liens don\'t prevent you from selling the property to us. These debts are typically paid off at closing from the sale proceeds. We\'ll work with the mortgage company and lien holders to get payoff amounts and ensure all debts are satisfied. If the property is underwater (worth less than what\'s owed), we can discuss options including short sales. Our team has experience handling complex situations involving multiple liens, tax debts, and other encumbrances.'
    },
    {
      question: 'How long does it take to sell an inherited house to you?',
      answer: 'Once probate is complete and you have legal authority to sell, we can close in as little as 7 days. The timeline depends on several factors including probate status, title issues, and your preferred closing date. If you need more time to sort through belongings or coordinate with other heirs, we can accommodate a longer timeline. We\'re flexible and work on your schedule. From initial contact to cash in hand, most of our inherited property transactions close within 2-4 weeks after probate completion.'
    },
    {
      question: 'Do I need to clean out the house before selling?',
      answer: 'No, you don\'t need to clean out the inherited property before selling to us. We buy houses with all contents included. If there are items you want to keep, you can remove them before closing, but you\'re not required to clear out everything. Many inherited homes contain decades of belongings, and sorting through everything can be emotionally exhausting and time-consuming. We handle all cleanup and disposal after purchase, saving you the stress and expense of dealing with it yourself.'
    },
    {
      question: 'What if the inherited house needs major repairs?',
      answer: 'We buy inherited homes in any condition, including properties that need major repairs. You don\'t need to fix anything before selling. Whether the house needs a new roof, has foundation issues, outdated electrical and plumbing, or hasn\'t been maintained in years, we\'ll make you a fair cash offer based on the property\'s current condition. We factor repair costs into our offer, so you get a realistic price without having to invest your own money in improvements.'
    },
    {
      question: 'Can I sell if I live out of state?',
      answer: 'Absolutely! Many of our clients live out of state and complete the entire transaction remotely. We can handle property access, inspections, and all necessary tasks in El Paso while you remain in your home state. Closing documents can be signed electronically or through a local notary in your area. We make the process as convenient as possible for out-of-state heirs, eliminating the need for multiple trips to Texas.'
    },
    {
      question: 'How do you determine your offer price?',
      answer: 'Our offer is based on several factors including the property\'s location, size, condition, current El Paso market conditions, and recent comparable sales in the area. We conduct a thorough assessment and provide a fair market value offer. Unlike traditional buyers, we don\'t lowball or try to renegotiate after inspection. Our initial offer is our final offer, and we explain exactly how we arrived at the price. We want you to feel confident that you\'re getting a fair deal.'
    },
    {
      question: 'What costs or fees will I have to pay?',
      answer: 'One of the biggest advantages of selling to us is that there are no real estate commissions, no closing costs, and no hidden fees. We cover all closing costs, including title insurance, escrow fees, and recording fees. The price we offer is the amount you receive at closing (minus any outstanding mortgages or liens that need to be paid off). There are no surprises or last-minute deductions. What we offer is what you get.'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-teal-100 rounded-full mb-6">
            <span className="text-teal-700 text-sm font-semibold">Common Questions</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions
          </h2>
          
          <p className="text-xl text-gray-600 leading-relaxed">
            Get answers to the most common questions about selling inherited properties in El Paso, Texas.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <h3 className="text-lg font-bold text-gray-900 pr-8">
                  {faq.question}
                </h3>
                <div className={`w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xl text-teal-600"></i>
                </div>
              </button>
              
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-8 pb-6">
                  <p className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-lg text-gray-600 mb-6">
            Still have questions? We're here to help.
          </p>
          <a
            href="tel:+1-915-206-5814"
            className="inline-flex items-center px-8 py-4 bg-teal-600 hover:bg-teal-700 text-white text-lg font-bold rounded-lg transition-all shadow-lg whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-fill mr-2"></i>
            Call Us: (915) 206-5814
          </a>
        </div>
      </div>
    </section>
  );
}